import client from "@/api/client"
import { ContentModerationRating, ImageSimpleDetail, ImageTag, PinnedItemType } from "@/api/sdk"
import IconButton from "@/components/IconButton"
import Toggle from "@/components/Toggle"
import Tooltip from "@/components/Tooltip"
import {
  DeleteIcon,
  DownloadIcon1,
  FolderMoveIcon,
  FolderTransferIcon,
  PinIcon,
  RefreshIcon,
  TickIcon,
  UnPinIcon,
} from "@/components/shared/icons"
import { useToast } from "@/hooks"
import { useDownloadImages } from "@/hooks/useDownloadImages"
import { googleAnalytics } from "@/lib/gtag"
import { usePinMutation, usePublicImageMutation } from "@/queries"
import { useDeleteItemStore, useMoveItemV2Store, useRecoverItemStore } from "@/stores"
import classNames from "classnames"
import { AnimatePresence, motion } from "framer-motion"
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react"
import MultiModeActions from "./TabFolder/MultiModeActions"
import ContextMenuZone from "./ContextMenuZone"

interface MultiImagesSelectBarProps {
  selectedItems: ImageSimpleDetail[]
  offsetLeft?: number
  parentFolderId?: string
  clear: () => void
  selectionMode?: boolean
  className?: string
  isFullActions?: boolean
  recipeId?: string
  children?: React.ReactNode
}

interface TagCount extends ImageTag {
  count: number
}

// const handleGetOnlySimilarTags = (tags: ImageTag[][]) => {
//   //concat all tags

//   if (!tags) return []

//   //get only similar tags count
//   const similarTagItems = tags.reduce((acc: TagCount[], tag) => {
//     tag?.forEach(t => {
//       const index = acc.findIndex(a => a.id === t.id)
//       if (index === -1) {
//         acc.push({ ...t, count: 1 })
//       } else {
//         acc[index].count = acc[index].count + 1 || 0
//       }
//     })
//     return acc
//   }, [])

//   const maxCountTag = similarTagItems.filter(tag => tag.count === tags.length)
//   return maxCountTag
// }

// const AddTagContainer = ({ selectedImages }: { selectedImages: ImageSimpleDetail[] }) => {
//   const [tags, setTags] = useState<PickTypeClass[]>([])
//   const [isAdd, setIsAdd] = useState(false)

//   const onAddTagSuccess = (tempId: number, newTag: string) => {
//     setTags([
//       ...tags,
//       {
//         id: tempId,
//         name: newTag,
//       },
//     ])
//   }

//   const onAddTagError = (tempId: number, error: string) => {
//     setTags(tags.filter(tag => tag.id !== tempId))
//   }

//   const toast = useToast()

//   const { mutate: mutateUntag } = useMutation({
//     mutationFn: ({ imageIds, tagId }: { imageIds: string[]; tagId: number }) =>
//       client.api.tagsControllerUntagImage({
//         imageIds,
//         tagIds: [tagId],
//       }),
//     onSuccess: (_, { imageIds }) => {
//       toast({
//         status: "success",
//         title: "Tag removed successfully",
//         message: [`Removed tags for ${imageIds.length} selected images successfully!`],
//       })
//     },
//     onMutate: ({ tagId }) => {
//       setTags(tags.filter(tag => tag.id !== tagId))
//     },
//     onSettled: () => {
//       refetchImagesFolder()
//     },
//   })

//   const handleUnTag = (tagId: number) => {
//     mutateUntag({ imageIds: selectedImages.map(image => image.id), tagId })
//   }

//   useEffect(() => {
//     if (selectedImages.length === 0) return
//     const similarTagItems = handleGetOnlySimilarTags(selectedImages.map(image => image.tags))

//     setTags(
//       similarTagItems.map(tag => ({
//         id: tag.id,
//         name: tag.name,
//       })),
//     )
//   }, [selectedImages, isAdd])

//   return (
//     <>
//       <div className="relative">
//         <IconButton
//           onClick={() => setIsAdd(prev => !prev)}
//           className="w-8 h-8 p-0 hover:bg-atherGray-700 active:bg-atherGray-800 text-atherGray-300 hover:text-atherGray-0"
//           colorScheme="transparent"
//         >
//           <TagIcon width={16} height={16} />
//         </IconButton>
//       </div>
//       <Modal
//         className="overflow-visible"
//         title="Add Images Tags"
//         isOpen={isAdd}
//         onClose={() => {
//           setIsAdd(false)
//         }}
//       >
//         <div>
//           <div className="p-1 bg-atherGray-800 border border-atherGray-600 rounded-lg">
//             <div className="w-full text-md flex flex-wrap rounded-lg">
//               {tags?.map(tag => (
//                 <ImageAttribute
//                   searchQuery={""}
//                   hiddenCopy
//                   value={tag.name}
//                   key={tag.id}
//                   isEdit
//                   onDeleteTag={() => handleUnTag(tag.id)}
//                 />
//               ))}
//               <AddTagInput
//                 tags={tags}
//                 imageIds={selectedImages.map(image => image.id)}
//                 onAddTag={onAddTagSuccess}
//                 onAddTagError={onAddTagError}
//               />
//             </div>
//           </div>
//         </div>
//       </Modal>
//     </>
//   )
// }

const MultiImagesSelectBar = ({
  parentFolderId,
  selectedItems,
  isFullActions = true,
  selectionMode,
  children,
  offsetLeft = 0,
  clear,
  recipeId,
  className,
}: MultiImagesSelectBarProps) => {
  const setDeletingItem = useDeleteItemStore(s => s.setDeletingItem)
  const setMovingItem = useMoveItemV2Store(s => s.setMovingItem)
  const [isPublic, setIsPublic] = useState(false)
  const isFetched = useRef(false)
  const [isDownloadingImage, setIsDownloadingImage] = useState(false)
  const downloadImages = useDownloadImages()
  const toast = useToast()
  const setRecoverItem = useRecoverItemStore(state => state.setRecoverItem)

  const { mutate: mutatePin, isPending: isPendingPin } = usePinMutation({
    onSuccess: () => {
      toast({
        status: "success",
        title: `Images ${selectedItems[0].pinned ? "unpinned" : "pinned"}`,
      })
      clear()
    },
  })

  const { mutate: mutatePublicImage } = usePublicImageMutation({
    onSuccess: async () => {},
  })

  const handleMoveImage = useCallback(() => {
    googleAnalytics.handleCategoryEvent({
      action: "click",
      params: {
        action: "Multi Image Move",
        ...selectedItems.reduce((acc, item, index) => {
          acc[`image_id_${index}`] = item.id
          return acc
        }, {}),
      },
    })

    setMovingItem({
      type: "image",
      onClose: clear,
      parentFolderId: parentFolderId,
      images: selectedItems.map(item => ({
        id: item.id,
        name: item.name,
        parentFolderId: item.folder?.id ?? null,
      })),
    })
  }, [selectedItems, parentFolderId, clear, setMovingItem])

  const handleCopyImage = useCallback(() => {
    googleAnalytics.handleCategoryEvent({
      action: "click",
      params: {
        action: "Multi Image Move",
        ...selectedItems.reduce((acc, item, index) => {
          acc[`image_id_${index}`] = item.id
          return acc
        }, {}),
      },
    })

    setMovingItem({
      type: "image",
      action: "copy",
      onClose: clear,
      parentFolderId: parentFolderId,
      images: selectedItems.map(item => ({
        id: item.id,
        name: item.name,
        parentFolderId: item.folder?.id ?? null,
      })),
    })
  }, [parentFolderId, clear, setMovingItem, selectedItems])

  const handlePinImage = useCallback(() => {
    googleAnalytics.handleCategoryEvent({
      action: "click",
      params: {
        action: "Multi Image Pin",
        ...selectedItems.reduce((acc, item, index) => {
          acc[`image_id_${index}`] = item.id
          return acc
        }, {}),
      },
    })

    mutatePin({
      ids: selectedItems.map(item => item.id),
      isPinned: !selectedItems[0].pinned,
      type: PinnedItemType.IMAGE,
      folderId: parentFolderId,
    })
  }, [selectedItems, mutatePin, parentFolderId])

  const handleDownload = useCallback(async () => {
    googleAnalytics.handleCategoryEvent({
      action: "click",
      params: {
        action: "Multi Image Download",
        ...selectedItems.reduce((acc, item, index) => {
          acc[`image_id_${index}`] = item.id
          return acc
        }, {}),
      },
    })

    setIsDownloadingImage(true)
    await downloadImages(
      selectedItems.map(i => i.url ?? ""),
      selectedItems.map(i => i.id),
      `${"download_ax"}_images`,
    ).finally(async () => {
      try {
        await client.api.imageControllerDownloadImage({
          imageIds: selectedItems.map(image => image.id),
        })
      } catch (error) {}

      setIsDownloadingImage(false)
    })
  }, [selectedItems, downloadImages])

  const handleRecoverImage = useCallback(() => {
    googleAnalytics.handleCategoryEvent({
      action: "click",
      params: {
        action: "Multi Image Recover",
        ...selectedItems.reduce((acc, item, index) => {
          acc[`image_id_${index}`] = item.id
          return acc
        }, {}),
      },
    })

    setRecoverItem({
      ids: selectedItems.map(item => item.id),
      parentFolderId: parentFolderId,
      deletedAt: selectedItems.find(item => item.deletedAt)?.deletedAt ?? "",
      type: "image",
      onClose: clear,
    })
  }, [parentFolderId, selectedItems, clear, setRecoverItem])

  const handleDeleteImage = useCallback(() => {
    googleAnalytics.handleCategoryEvent({
      action: "click",
      params: {
        action: "Multi Image Delete",
        ...selectedItems.reduce((acc, item, index) => {
          acc[`image_id_${index}`] = item.id
          return acc
        }, {}),
      },
    })

    setDeletingItem({
      type: "image",
      folderId: parentFolderId,
      ids: selectedItems.map(item => item.id),
      id: selectedItems.find(item => item.id)?.id ?? "",
      recipeParams: {
        recipeId,
      },
      deletedAt: selectedItems.find(item => item.deletedAt)?.deletedAt ?? "",
      onClose: clear,
    })
  }, [parentFolderId, selectedItems, clear, setDeletingItem, recipeId])

  useEffect(() => {
    if (!isFetched.current) return

    const timer = setTimeout(() => {
      mutatePublicImage({
        ids: selectedItems.map(item => item.id),
        isDiscoverable: isPublic,
        folderId: parentFolderId,
      })
    }, 250)

    return () => clearTimeout(timer)
  }, [isPublic])

  useEffect(() => {
    isFetched.current = false
    setIsPublic(selectedItems.every(i => i.isDiscoverable) || false)
  }, [selectedItems])

  const renderImageActions = useMemo(() => {
    return (
      <MultiModeActions
        onDownload={{
          fn: handleDownload,
          enabled: selectedItems.length > 0,
          visible: true,
          title: `Download`,
        }}
        onCopy={{
          fn: handleCopyImage,
          enabled: !selectedItems.some(item => !item.capabilities?.canUpdate) && selectedItems.length > 0,
          visible: !selectedItems.find(item => item.deletedAt)?.deletedAt,
          title: `Copy ${selectedItems.length > 1 ? "images" : "image"}`,
        }}
        onMove={{
          fn: handleMoveImage,
          enabled: !selectedItems.some(item => !item.capabilities?.canMove) && selectedItems.length > 0,
          visible: !selectedItems.find(item => item.deletedAt)?.deletedAt,
          title: `Move ${selectedItems.length > 1 ? "images" : "image"}`,
        }}
        onPin={{
          fn: handlePinImage,
          enabled: !selectedItems.some(item => !item.capabilities?.canView) && selectedItems.length > 0,
          visible: !selectedItems.find(item => item.deletedAt)?.deletedAt,
          title: `${selectedItems[0]?.pinned ? "Unpin" : "Pin"}`,
          icon: selectedItems[0]?.pinned ? <UnPinIcon width={20} height={20} /> : <PinIcon width={20} height={20} />,
        }}
        onRecover={{
          fn: handleRecoverImage,
          enabled: !selectedItems.some(item => !item.capabilities?.canRecover) && selectedItems.length > 0,
          visible: !!selectedItems.find(item => item.deletedAt)?.deletedAt,
          title: `Recover`,
        }}
        onDelete={{
          fn: handleDeleteImage,
          enabled: !selectedItems.some(item => !item.capabilities?.canDelete) && selectedItems.length > 0,
          visible: true,
          title: `Delete`,
        }}
      />
    )
  }, [
    selectedItems,
    handleCopyImage,
    handleDeleteImage,
    handleDownload,
    handleMoveImage,
    handlePinImage,
    handleRecoverImage,
  ])

  return (
    <Fragment>
      <AnimatePresence
        onExitComplete={() => {
          setIsDownloadingImage(false)
        }}
      >
        {selectedItems.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed -translate-x-1/2 top-[3rem] md:top-32 z-20"
            style={{
              left: `calc(50% + ${offsetLeft}px)`,
            }}
          >
            <div className="rounded-2xl bg-atherGray-850 py-2 px-2 md:px-4 text-xs font-semibold text-atherGray-300 h-12 flex items-center space-x-4">
              <div className="pl-2 flex items-center">
                {selectedItems.length} <span className="hidden md:inline-block ml-2">selected</span>
                <span className="inline-block ml-2 md:hidden">
                  <TickIcon />
                </span>
              </div>
              <hr className="w-[1px] h-6 border border-atherGray-800" />
              {/* <button>Select all</button> */}

              {!selectedItems.find(item => item.deletedAt)?.deletedAt && isFullActions && (
                <>
                  <div
                    className={classNames("flex items-center", {
                      "opacity-50 cursor-not-allowed": selectedItems.some(i => !i.capabilities?.canUpdate),
                    })}
                  >
                    <Toggle
                      disabled={selectedItems.some(
                        i =>
                          !i.capabilities?.canUpdate ||
                          i.moderationRating === ContentModerationRating.HardcoreNsfw ||
                          i.moderationRating === ContentModerationRating.SemiNsfw,
                      )}
                      labelClassName="text-atherGray-300 text-xs"
                      sizeToggle="sm"
                      name={"Public"}
                      checked={isPublic}
                      onChange={e => {
                        e.stopPropagation()

                        googleAnalytics.handleCategoryEvent({
                          action: "click",
                          params: {
                            action: "Multi Image Share Explore",
                            ...selectedItems.reduce((acc, item, index) => {
                              acc[`image_id_${index}`] = item.id
                              return acc
                            }, {}),
                          },
                        })

                        if (
                          selectedItems.some(
                            i =>
                              !i.capabilities?.canUpdate ||
                              i.moderationRating === ContentModerationRating.HardcoreNsfw ||
                              i.moderationRating === ContentModerationRating.SemiNsfw,
                          )
                        )
                          return

                        isFetched.current = true
                        setIsPublic(prev => !prev)
                      }}
                    />
                    <p className="text-xs ml-2 flex items-center">Explore</p>
                  </div>
                  <hr className="w-[1px] h-6 border border-atherGray-800" />
                </>
              )}

              <div className="flex items-center space-x-2">
                {!selectedItems.find(item => item.deletedAt)?.deletedAt && isFullActions && (
                  <>
                    <Tooltip
                      trigger={
                        <IconButton
                          disabled={selectedItems.some(i => !i.capabilities?.canMove)}
                          className="w-8 h-8 p-0 hover:bg-atherGray-700 active:bg-atherGray-800 text-atherGray-300 hover:text-atherGray-0"
                          colorScheme="transparent"
                          onClick={() => {
                            handleMoveImage()
                          }}
                        >
                          <FolderMoveIcon width={16} height={16} />
                        </IconButton>
                      }
                    >
                      Move {selectedItems.length > 1 ? "images" : "image"}
                    </Tooltip>
                    <Tooltip
                      trigger={
                        <IconButton
                          disabled={selectedItems.some(i => !i.capabilities?.canUpdate)}
                          className="w-8 h-8 p-0 hover:bg-atherGray-700 active:bg-atherGray-800 text-atherGray-300 hover:text-atherGray-0"
                          colorScheme="transparent"
                          onClick={() => {
                            handleCopyImage()
                          }}
                        >
                          <FolderTransferIcon width={16} height={16} />
                        </IconButton>
                      }
                    >
                      Copy {selectedItems.length > 1 ? "images" : "image"}
                    </Tooltip>
                    {selectedItems.some(i => i.pinned !== undefined) && (
                      <Tooltip
                        trigger={
                          <IconButton
                            className="w-8 h-8 p-0 bg-atherGray-850 hover:bg-atherGray-700 active:bg-atherGray-800 text-atherGray-300 hover:text-atherGray-0"
                            colorScheme="transparent"
                            isLoading={isPendingPin}
                            onClick={() => {
                              handlePinImage()
                            }}
                          >
                            {!selectedItems[0].pinned ? (
                              <PinIcon width={16} height={16} />
                            ) : (
                              <UnPinIcon width={16} height={16} />
                            )}
                          </IconButton>
                        }
                      >
                        {selectedItems[0].pinned ? "Unpin" : "Pin"}
                      </Tooltip>
                    )}
                  </>
                )}
                <Tooltip
                  trigger={
                    <IconButton
                      isLoading={isDownloadingImage}
                      className="w-8 h-8 p-0 bg-atherGray-850 hover:bg-atherGray-700 active:bg-atherGray-800 text-atherGray-300 hover:text-atherGray-0"
                      colorScheme="transparent"
                      onClick={async () => {
                        handleDownload()
                      }}
                    >
                      <DownloadIcon1 width={16} height={16} />
                    </IconButton>
                  }
                >
                  Download
                </Tooltip>
                {selectedItems.find(item => item.deletedAt)?.deletedAt && (
                  <Tooltip
                    trigger={
                      <IconButton
                        disabled={selectedItems.some(i => !i.capabilities?.canRecover)}
                        className="w-8 h-8 p-0 bg-atherGray-850 hover:bg-atherGray-700 active:bg-atherGray-800 text-atherGray-300 hover:text-atherGray-0"
                        colorScheme="transparent"
                        onClick={() => {
                          handleRecoverImage()
                        }}
                      >
                        <RefreshIcon className="rotate-180" width={16} height={16} />
                      </IconButton>
                    }
                  >
                    Recover
                  </Tooltip>
                )}
                {isFullActions && (
                  <Tooltip
                    trigger={
                      <IconButton
                        disabled={selectedItems.some(i => !i.capabilities?.canDelete)}
                        className="w-8 h-8 p-0 hover:bg-atherGray-700 active:bg-atherGray-800 text-red-500"
                        colorScheme="transparent"
                        onClick={() => {
                          handleDeleteImage()
                        }}
                      >
                        <DeleteIcon width={16} height={16} />
                      </IconButton>
                    }
                  >
                    Delete
                  </Tooltip>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      {children && (
        <ContextMenuZone isDisabled={!selectionMode} className="flex-1 flex flex-col" contextMenu={renderImageActions}>
          {children}
        </ContextMenuZone>
      )}
    </Fragment>
  )
}

export default MultiImagesSelectBar
